"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { useTranslation } from "../language/hooks/useTranslation";
import { useAppRating } from "@/hooks/use-app-rating";
import { Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="flex items-center gap-0.5">
      <span className="sr-only">{rating.toFixed(1)} out of 5 stars</span>
      {Array.from({ length: 5 }, (_, i) => {
        const fill = Math.min(1, Math.max(0, rating - i));
        return (
          <span key={i} className="relative inline-block w-3.5 h-3.5 overflow-hidden" aria-hidden="true">
            <svg viewBox="0 0 12 12" className="absolute inset-0 w-3.5 h-3.5 text-brand-stroke" fill="currentColor">
              <path d="M6 1l1.3 2.6 2.9.4-2.1 2 .5 2.9L6 7.5 3.4 8.9l.5-2.9-2.1-2 2.9-.4z" />
            </svg>
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <svg viewBox="0 0 12 12" className="w-3.5 h-3.5 text-amber-400" fill="currentColor">
                <path d="M6 1l1.3 2.6 2.9.4-2.1 2 .5 2.9L6 7.5 3.4 8.9l.5-2.9-2.1-2 2.9-.4z" />
              </svg>
            </span>
          </span>
        );
      })}
    </span>
  );
}

const PLATFORM_LABEL: Record<string, string> = {
  ios: "App Store",
  android: "Google Play",
};

export const storeLinks = [
  {
    name: "App Store",
    href: "https://apps.apple.com/au/app/vell-money-transfer/id6748893220",
    src: "/Download_on_the_App_Store_RGB_blk.svg.webp",
    width: 180,
    height: 60,
  },
  {
    name: "Google Play",
    href: "https://play.google.com/store/apps/details?id=com.remitcircle.app",
    src: "/google-play-badge-2-scaled.png",
    width: 180,
    height: 54,
  },
  {
    name: "AppGallery",
    href: "https://appgallery.huawei.com/#/app/C115220039",
    src: "/huawei_appgallery_badge-logo.png",
    width: 180,
    height: 54,
  },
];

export default function AppDownloadSection() {
  const { t, tm } = useTranslation();
  const tooltips = tm<string[]>("dashboard.download.card.tooltip", []);
  const { ratings, isLoading } = useAppRating();

  return (
    <section className="relative section-padding bg-brand-bg bg-cover bg-center bg-no-repeat">
      <div className="absolute inset-0 w-full h-full z-0">
        {/* The only video on the site that was missing preload="metadata"; every
            other one sets it. This one sits below the fold behind an overlay, so
            there is nothing to gain from fetching it ahead of the page. */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="w-full h-full object-cover">
          <source src="/videos/wave-animation-boomerang.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/20" />
      </div>

      <div className="flex justify-center mx-auto lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] lg:px-0">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.05 }}
          className="relative z-10 w-full max-w-6xl"
        >
          <div className="rounded-3xl border border-brand-white/35 shadow-xl shadow-black/30">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.2 }}
              className="flex flex-col items-center gap-6 xs:gap-8 2xl:gap-10 px-4 py-7 sm:py-8 sm:py-12 sm:px-10"
            >

              <motion.h2
                variants={itemVariants}
                className="text-brand-text text-2xl xs:text-3xl sm:text-4xl xl:text-6xl leading-[1] font-bold mb-0 text-center"
              >
                {t("dashboard.download.title")}
              </motion.h2>
              <motion.div
                variants={itemVariants}
                className="hidden xs:flex flex-col gap-4 bg-brand-black py-6 sm:px-6 sm:py-8 input-border-radius max-w-[300px] 2xl:max-w-[350px] w-full"
              >
                <p className="font-semibold text-center text-brand-text text-xl md:text-2xl 3xl:text-3xl">
                  {t("dashboard.download.card.title")}
                </p>
                <div className="relative mx-auto aspect-square w-full max-w-[150px] lg:max-w-[170px] overflow-hidden global-border-radius bg-brand-card border border-brand-stroke p-2">
                  <Image
                    src="/QR-Download.png"
                    alt="QR code to download the Vell app"
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 150px, 170px"
                    priority
                    fetchPriority="high"
                  />
                </div>

                <p className="text-xs text-center text-brand-text/60 3xl:text-base mt-2">
                  {t("dashboard.download.card.note")}
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2 justify-center">
                {storeLinks.map((link) => (
                  <Link
                    key={link.name}
                    href={link.href}
                    className="group inline-flex w-[135px] xs:w-[150px] md:w-[170px] items-center justify-center global-border-radius transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                    aria-label={`Open ${link.name}`}
                  >
                    <Image
                      src={link.src}
                      alt={link.name}
                      width={link.width}
                      height={link.height}
                      className="w-full max-w-[135px] xs:max-w-[150px] max-w-[125px] min-h-[48px] xs:min-h-[50px] sm:min-h-[52px]"
                      sizes="(max-width: 640px) 176px, 176px"
                      priority={link.name === "App Store"}
                    />
                  </Link>
                ))}
              </motion.div>

              {isLoading ? (
                <motion.div variants={itemVariants} className="flex flex-wrap gap-3 justify-center">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <RatingSkeleton key={i} />
                  ))}
                </motion.div>
              ) : (
                ratings.length > 0 && (
                  <motion.div variants={itemVariants} className="flex flex-wrap gap-3 justify-center">
                    {ratings.map((r) => (
                      <div
                        key={r.platform}
                        className="flex flex-col gap-1.5 global-border-radius border border-brand-stroke/40 bg-brand-card p-3 xs:px-4 xs:py-3"
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">
                          {PLATFORM_LABEL[r.platform]}
                        </span>
                        <div className="flex items-center gap-2">
                          <StarRating rating={r.rating} />
                          <span className="text-xs sm:text-sm font-bold text-brand-text">
                            {r.rating.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )
              )}

            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function RatingSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 global-border-radius border border-brand-stroke/40 bg-brand-card p-3 xs:px-4 xs:py-3 animate-pulse">
      <div className="h-2.5 w-16 rounded bg-brand-stroke/40" />
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-20 rounded bg-brand-stroke/40" />
        <div className="h-3 w-6 rounded bg-brand-stroke/40" />
      </div>
    </div>
  );
}